"use client";

import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Bar,
  BarChart,
  Line,
  LineChart,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Legend,
} from "recharts";
import { Users, UserCheck, UserMinus } from "lucide-react";
import { useNavigate } from "react-router-dom";
import DashboardFilters from "../../Component/dashboard-filters";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "../../Component/ui/chart";
import API_URL from "../../../src/Config";

export function HumanResourceSection() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);

  const [summary, setSummary] = useState({
    total_workforce_assigned: { count: 0, label: "" },
    available_manpower: {
      count: 0,
      availability_rate: 0,
      breakdown: { present_at_site: 0, appointment_issued: 0 },
    },
    balance_position: { count: 0, label: "" },
  });

  const [charts, setCharts] = useState({
    workforce_distribution: { assigned: 0, available: 0, balance: 0 },
    available_manpower_trend: [],
    project_wise_distribution: [],
    status_distribution: [],
  });

  // ✅ Centralized filter state (matching PM pattern)
  const [filters, setFilters] = useState({
    project_id: "",
    date_preset: "all",
    showCustomDate: false,
    start_date: "",
    end_date: "",
  });

  // ✅ Fetch data with filters (matching PM pattern)
  const fetchHRMData = async (currentFilters = filters) => {
    try {
      setLoading(true);
      const authKey = localStorage.getItem("authKey");

      const params = new URLSearchParams();
      if (currentFilters.date_preset)
        params.append("filter_option", currentFilters.date_preset);
      if (currentFilters.project_id)
        params.append("project_id", currentFilters.project_id);
      if (currentFilters.start_date)
        params.append("start_date", currentFilters.start_date);
      if (currentFilters.end_date)
        params.append("end_date", currentFilters.end_date);

      const response = await axios.get(
        `${API_URL}/api/kpi/human-resource-management?${params.toString()}`,
        {
          headers: { Authorization: authKey },
        },
      );

      if (response.status === 200) {
        const data = response.data;
        setSummary(data.summary || {});
        setCharts(data.charts || {});
      }
    } catch (err) {
      console.error("Error fetching HRM KPIs:", err);
    } finally {
      setLoading(false);
    }
  };

  // ✅ Apply filters (matching PM pattern)
  const handleApplyFilters = (newFilters) => {
    setFilters(newFilters);
    fetchHRMData(newFilters);
  };

  // ✅ Reset filters (matching PM pattern)
  const handleResetFilters = () => {
    const reset = {
      project_id: "",
      date_preset: "all",
      showCustomDate: false,
      start_date: "",
      end_date: "",
    };
    setFilters(reset);
    fetchHRMData(reset);
  };

  useEffect(() => {
    fetchHRMData();
  }, []);

  // Card click handler
  const handleCardClick = (listType) => {
    const params = new URLSearchParams();
    params.append("list_type", listType);

    const filterOptionValue = filters.date_preset ? filters.date_preset : "All";
    params.append("filter_option", filterOptionValue);

    if (filters.project_id && filters.project_id.trim() !== "") {
      params.append("project_id", filters.project_id);
    }

    if (
      filters.start_date &&
      filters.end_date &&
      filters.start_date.trim() !== "" &&
      filters.end_date.trim() !== ""
    ) {
      params.append("start_date", filters.start_date);
      params.append("end_date", filters.end_date);
    }

    console.log("Navigating to HRM List with params:", params.toString());

    window.open(`/hrm-list?${params.toString()}`, "_blank");
    // navigate(`/hrm-list?${params.toString()}`);
  };

  // Prepare chart data
  const workforceData = [
    {
      category: "Total",
      assigned: charts.workforce_distribution.assigned,
      available: charts.workforce_distribution.available,
      balance: charts.workforce_distribution.balance,
    },
  ];

  const manpowerTrendData = charts.available_manpower_trend.map((m) => ({
    month: m.month,
    available: m.available,
  }));

  const departmentWorkforceData = charts.project_wise_distribution.map((p) => ({
    department: p.project,
    assigned: p.assigned,
    available: p.available,
    balance: p.balance,
  }));

  return (
    <div className="newdashboard-hr-section">
      {loading ? (
        <div className="loader-container">
          <div className="loader"></div>
          <div>Loading...</div>
        </div>
      ) : (
        <>
          {/* Header + Filters */}
          <div className="newdashboard-hr-header">
            <div>
              <h2 className="newdashboard-section-title">
                Human Resource Management
              </h2>
              <p className="newdashboard-hr-subtitle">
                Monitor workforce allocation and availability
              </p>
            </div>

            {/* ✅ Controlled Filters (matching PM pattern) */}
            <DashboardFilters
              filters={filters}
              onFilterChange={setFilters}
              onApply={handleApplyFilters}
              onReset={handleResetFilters}
            />
          </div>

          {/* KPI Cards - Now Clickable */}
          <div className="newdashboard-hr-kpi-grid">
            {/* Total Workforce Assigned */}
            <div
              className="newdashboard-hr-card newdashboard-card-clickable"
              onClick={() => handleCardClick("required_positions")}
              style={{ cursor: "pointer" }}
            >
              <div className="newdashboard-hr-card-header">
                <h3 className="newdashboard-hr-card-title">
                  Total Workforce Required
                </h3>
                <Users className="newdashboard-hr-card-icon" />
              </div>
              <div className="newdashboard-hr-card-content">
                <div
                  className="newdashboard-hr-kpi-value"
                  style={{ color: "hsl(217, 91%, 60%)" }}
                >
                  {summary.total_workforce_assigned.count}
                </div>
                <p className="newdashboard-hr-kpi-note">
                  {summary.total_workforce_assigned.label}
                </p>
              </div>
            </div>

            {/* Available Manpower */}
            <div
              className="newdashboard-hr-card newdashboard-card-clickable"
              onClick={() => handleCardClick("available_manpower")}
              style={{ cursor: "pointer" }}
            >
              <div className="newdashboard-hr-card-header">
                <h3 className="newdashboard-hr-card-title">
                  Available Manpower
                </h3>
                <UserCheck className="newdashboard-hr-card-icon" />
              </div>
              <div className="newdashboard-hr-card-content">
                <div
                  className="newdashboard-hr-kpi-value"
                  style={{ color: "hsl(142, 71%, 45%)" }}
                >
                  {summary.available_manpower.count}
                </div>
                <p className="newdashboard-hr-kpi-note">
                  {summary.available_manpower.availability_rate}% availability
                  rate
                </p>
              </div>
            </div>

            {/* Balance Position */}
            <div
              className="newdashboard-hr-card newdashboard-card-clickable"
              onClick={() => handleCardClick("pending_positions")}
              style={{ cursor: "pointer" }}
            >
              <div className="newdashboard-hr-card-header">
                <h3 className="newdashboard-hr-card-title">Balance Position</h3>
                <UserMinus className="newdashboard-hr-card-icon" />
              </div>
              <div className="newdashboard-hr-card-content">
                <div
                  className="newdashboard-hr-kpi-value"
                  style={{ color: "hsl(25, 95%, 53%)" }}
                >
                  {summary.balance_position.count}
                </div>
                <p className="newdashboard-hr-kpi-note">
                  {summary.balance_position.label}
                </p>
              </div>
            </div>
          </div>

          {/* Charts */}
          <div className="newdashboard-hr-charts">
            {/* Workforce Distribution */}
            <div className="newdashboard-hr-chart-card">
              <div className="newdashboard-hr-chart-header">
                <h3 className="newdashboard-hr-chart-title">
                  Workforce Distribution
                </h3>
                <p className="newdashboard-hr-chart-description">
                  Assigned vs Available vs Balance
                </p>
              </div>
              <div className="newdashboard-hr-chart-content">
                {workforceData.some(
                  (d) => d.assigned > 0 || d.available > 0 || d.balance > 0,
                ) ? (
                  <ChartContainer
                    config={{
                      assigned: {
                        label: "Assigned",
                        color: "hsl(217, 91%, 60%)",
                      },
                      available: {
                        label: "Available",
                        color: "hsl(142, 71%, 45%)",
                      },
                      balance: { label: "Balance", color: "hsl(25, 95%, 53%)" },
                    }}
                    className="newdashboard-hr-chart"
                  >
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={workforceData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="category" />
                        <YAxis />
                        <ChartTooltip content={<ChartTooltipContent />} />
                        <Legend />
                        <Bar
                          dataKey="assigned"
                          fill="hsl(217, 91%, 60%)"
                          stackId="a"
                          name="Assigned"
                        />
                        <Bar
                          dataKey="available"
                          fill="hsl(142, 71%, 45%)"
                          stackId="a"
                          name="Available"
                        />
                        <Bar
                          dataKey="balance"
                          fill="hsl(25, 95%, 53%)"
                          stackId="a"
                          name="Balance"
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </ChartContainer>
                ) : (
                  <div className="newdashboard-no-data">No Data Found</div>
                )}
              </div>
            </div>

            {/* Available Manpower Trend */}
            <div className="newdashboard-hr-chart-card">
              <div className="newdashboard-hr-chart-header">
                <h3 className="newdashboard-hr-chart-title">
                  Available Manpower Trend
                </h3>
                <p className="newdashboard-hr-chart-description">
                  Availability over time
                </p>
              </div>
              <div className="newdashboard-hr-chart-content">
                {manpowerTrendData.length > 0 ? (
                  <ChartContainer
                    config={{
                      available: {
                        label: "Available",
                        color: "hsl(142, 71%, 45%)",
                      },
                    }}
                    className="newdashboard-hr-chart"
                  >
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={manpowerTrendData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="month" />
                        <YAxis />
                        <ChartTooltip content={<ChartTooltipContent />} />
                        <Legend />
                        <Line
                          type="monotone"
                          dataKey="available"
                          stroke="hsl(142, 71%, 45%)"
                          strokeWidth={2}
                          name="Available"
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </ChartContainer>
                ) : (
                  <div className="newdashboard-no-data">No Data Found</div>
                )}
              </div>
            </div>
          </div>

          {/* Department Breakdown */}
          <div className="newdashboard-hr-department">
            <div className="newdashboard-hr-department-header">
              <h3 className="newdashboard-hr-department-title">
                Project-wise Workforce Distribution
              </h3>
              <p className="newdashboard-hr-department-description">
                Workforce allocation across projects
              </p>
            </div>
            <div className="newdashboard-hr-department-content">
              {departmentWorkforceData.length > 0 ? (
                <ChartContainer
                  config={{
                    assigned: {
                      label: "Assigned",
                      color: "hsl(217, 91%, 60%)",
                    },
                    available: {
                      label: "Available",
                      color: "hsl(142, 71%, 45%)",
                    },
                    balance: { label: "Balance", color: "hsl(25, 95%, 53%)" },
                  }}
                  className="newdashboard-hr-chart"
                >
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={departmentWorkforceData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="department" />
                      <YAxis />
                      <ChartTooltip content={<ChartTooltipContent />} />
                      <Legend />
                      <Bar
                        dataKey="assigned"
                        fill="hsl(217, 91%, 60%)"
                        name="Assigned"
                      />
                      <Bar
                        dataKey="available"
                        fill="hsl(142, 71%, 45%)"
                        name="Available"
                      />
                      <Bar
                        dataKey="balance"
                        fill="hsl(25, 95%, 53%)"
                        name="Balance"
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </ChartContainer>
              ) : (
                <div className="newdashboard-no-data">No Data Found</div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
